import {
  AppointmentConfigs,
  QUEUE_TYPE,
  QueueConfigType,
  TPostQueueConfigUserPreference,
} from "@elixir/types";
import { getListStyle, getStyle, reorder, swap } from "@elixir/utils";
import React, { useEffect } from "react";
import {
  DragDropContext,
  Draggable,
  DraggableProvided,
  DropResult,
  Droppable,
} from "react-beautiful-dnd";
import { FieldItemComponentUi } from "ui";
import useGetQueueConfig from "../../../apis/tryst/get-queue-config";
import usePostQueueConfigUserPreference from "../../../apis/tryst/post-queue-config-user-preference";
import { ReactComponent as CircleInfoRegularIcon } from "../../../assets/icons/font-awesome/circle-info-regular.svg";
import { ReactComponent as XmarkRegularIcon } from "../../../assets/icons/font-awesome/xmark-regular.svg";
import TooltipComponent from "../../../organisms/common/tooltip-component";
import { DEFAULT_COLUMNS } from "../../../organisms/queue/show-config-selected-columns";
import useQueueStore from "../../../stores/queue-store";
import { QueueConfigItemType } from "../../../types/appointment";
import {
  getActionsItemList,
  getColumnItemList,
} from "../../../utils/queue-utils";
import { track } from "../../../utils/tracking";
import Tabs from "./tabs";
import { DEFAULT_ACTIONS } from "../../../organisms/queue/right-panel-card-bottom-action-buttons";

const defaultState = (
  config: QueueConfigType | undefined
): TPostQueueConfigUserPreference["preferences"] => {
  return {
    actions:
      (config?.actions?.length || 0) > 0 ? config?.actions! : DEFAULT_ACTIONS,
    columns:
      (config?.columns?.length || 0) > 0 ? config?.columns! : DEFAULT_COLUMNS,
  };
};

const CustomiseYourQueue = () => {
  const rightPanelQueueType = useQueueStore(
    (state) => state.rightPanelQueueType
  ) as keyof AppointmentConfigs["user_preferences"];

  const [tab, setTab] = React.useState<
    keyof AppointmentConfigs["user_preferences"]
  >(() => rightPanelQueueType);

  const { data: queueConfig } = useGetQueueConfig();
  const [items, setItems] = React.useState<QueueConfigType>(() =>
    defaultState(queueConfig?.data?.["user_preferences"][tab])
  );

  useEffect(() => {
    setItems(defaultState(queueConfig?.data?.["user_preferences"][tab]));
  }, [queueConfig?.data, tab]);

  const { mutateAsync: postQueueConfig } = usePostQueueConfigUserPreference();

  const setShowCustomiseYourQueue = useQueueStore(
    (state) => state.setShowCustomiseYourQueue
  );

  const onClose = () => {
    track("queue_customise_your_queue_cancel", {
      tab,
    });
    setShowCustomiseYourQueue(false);
  };

  const handleSave = async () => {
    track("queue_customise_your_queue_save", {
      tab,
    });
    await postQueueConfig({
      preferenceFor: tab,
      preferences: items,
    });
    onClose();
  };

  const onChange = (key: keyof AppointmentConfigs["user_preferences"]) => {
    track("queue_customise_your_queue_tab_change", {
      tab: key,
    });
    setTab(key);
  };

  const handleItemSelect = (type: "columns" | "actions", items: string[]) => {
    setItems((prev) => {
      if (!prev) return prev;

      return {
        ...prev,
        [type]: items,
      };
    });
  };

  return (
    <div
      className="dr-bg-darwin-touch-neutral-0 dr-rounded-8 dr-overflow-hidden dr-flex dr-flex-col dr-min-h-[80vh] dr-min-w-[80vw] dr-max-h-[80vh]"
      onClick={(e) => e.stopPropagation()}
    >
      <div className="dr-text-darwin-touch-neutral-1000 dr-flex dr-items-center dr-w-full dr-justify-between dr-p-16">
        <div className="BodyBody1Bold dr-px-8">Customise your queue</div>
        <div
          onClick={onClose}
          className="dr-flex dr-items-center dr-justify-center dr-p-8 dr-cursor-pointer dr-rounded-full ripple dr-max-w-min"
        >
          <XmarkRegularIcon className="dr-w-16 dr-h-16" />
        </div>
      </div>
      <Tabs<keyof AppointmentConfigs["user_preferences"]>
        underline
        showDivider
        className="dr-flex-1 dr-flex-col"
        onChange={onChange}
        activeKey={tab}
        bodyClassName="dr-flex-1"
        tabsContainerClassName="dr-px-16 dr-space-x-16"
        items={[
          {
            label: "My OPD Queue",
            key: QUEUE_TYPE.CHECKED_IN,
            children: (
              <div className="dr-w-full dr-flex">
                <RenderConfigList
                  tab={tab}
                  tooltipText="The number of columns displayed may vary based on your screen size. 
                  Your display can accommodate upto 3 columns"
                  className="dr-flex-1"
                  title="Sort Column preference"
                  subtitle="3 Columns Visible"
                  list={getSortedItems(
                    getColumnItemList(QUEUE_TYPE.CHECKED_IN),
                    items.columns
                  )}
                  onClick={(items) => handleItemSelect("columns", items)}
                />
                <div className="dr-w-2 dr-min-h-full dr-bg-darwin-touch-neutral-200"></div>
                <RenderConfigList
                  tab={tab}
                  className="dr-flex-1"
                  tooltipText="The number of actions displayed may vary based on your screen size. 
                  Your display can accommodate upto 3 actions"
                  list={getSortedItems(
                    getActionsItemList(QUEUE_TYPE.CHECKED_IN),
                    items.actions
                  )}
                  title="Sort Action preference"
                  subtitle="3 Actions Visible"
                  onClick={(items) => handleItemSelect("actions", items)}
                />
              </div>
            ),
          },
          {
            label: "Completed Queue",
            key: QUEUE_TYPE.COMPLETED,
            children: (
              <div className="dr-w-full dr-flex ">
                <RenderConfigList
                  tab={tab}
                  className="dr-flex-1"
                  tooltipText="The number of columns displayed may vary based on your screen size. 
                  Your display can accommodate upto 3 columns"
                  title="Sort Column preference"
                  subtitle="3 Columns Visible"
                  list={getSortedItems(
                    getColumnItemList("completed"),
                    items.columns
                  )}
                  onClick={(items) => handleItemSelect("columns", items)}
                />
                <div className="dr-w-2 dr-min-h-full dr-bg-darwin-touch-neutral-200"></div>
                <RenderConfigList
                  tab={tab}
                  className="dr-flex-1"
                  tooltipText="The number of actions displayed may vary based on your screen size. 
                  Your display can accommodate upto 3 actions"
                  title="Sort Action preference"
                  subtitle="3 Actions Visible"
                  list={getSortedItems(
                    getActionsItemList("completed"),
                    items.actions
                  )}
                  onClick={(items) => handleItemSelect("actions", items)}
                />
              </div>
            ),
          },
        ]}
      />
      <div className="dr-sticky dr-bottom-0 dr-bg-darwin-touch-neutral-0 dr-shadow-elevation-700 dr-py-14 dr-px-24 dr-flex dr-items-center dr-justify-between BodyBody2Bold ">
        <button className="border-none dr-rounded-8" onClick={onClose}>
          Cancel
        </button>
        <button
          onClick={handleSave}
          className="dr-rounded-8 dr-px-52 dr-h-40 dr-text-doctor-darwin-neutral-0 dr-bg-doctor-darwin-accent-bright-blue-800 hover:dr-text-doctor-darwin-neutral-0 hover:dr-bg-doctor-darwin-accent-bright-blue-800 focus:dr-bg-doctor-darwin-accent-bright-blue-800 focusdr-text-doctor-darwin-neutral-0"
        >
          Save
        </button>
      </div>
    </div>
  );
};

export default CustomiseYourQueue;

const getSortedItems = (list: QueueConfigItemType[] = [], items: string[]) => {
  const a = items.map((id) => ({
    ...list.find((l) => l.columnId === id),
    isSelected: true,
  }));
  const b = list.filter((i) => !items.includes(i.columnId));
  return [...a, ...b] as QueueConfigItemType[];
};

function Item({
  tab,
  provided,
  item,
  style,
  isDragging,
  onClick,
}: {
  tab?: keyof AppointmentConfigs["user_preferences"];
  item: QueueConfigItemType;
  provided: DraggableProvided;
  style?: React.CSSProperties;
  isDragging?: boolean;
  onClick: (items: string, vote: "UP" | "DOWN") => void;
}) {
  return (
    <div
      key={`${item?.columnId}-${tab}`}
      {...provided.draggableProps}
      {...provided.dragHandleProps}
      ref={provided.innerRef}
      style={getStyle({ provided, style, isDragging })}
      className={`dr-py-8 dr-z-100 dr-px-16 ${
        item.isSelected && !isDragging ? "dr-bg-darwin-green-bg-light" : ""
      } ${isDragging ? "is-dragging dr-rounded-8" : ""}`}
    >
      <FieldItemComponentUi
        subtitle={item.subtitle}
        title={item.title}
        upVote={() => onClick(item.columnId, "UP")}
        downVote={() => onClick(item.columnId, "DOWN")}
      />
    </div>
  );
}

const RenderConfigList = ({
  tab,
  title,
  subtitle,
  list,
  onClick,
  className = "",
  tooltipText = "",
}: {
  tab: keyof AppointmentConfigs["user_preferences"];
  title: string;
  className?: string;
  subtitle: string;
  list: QueueConfigItemType[];
  tooltipText?: string;
  onClick: (items: string[]) => void;
}) => {
  const [allItemsList, setAllItemsList] = React.useState<QueueConfigItemType[]>(
    () => list
  );
  useEffect(() => {
    setAllItemsList(list);
  }, [list, tab]);

  const handleVote = (itemId: string, vote: "UP" | "DOWN") => {
    setAllItemsList((prev) => {
      const itemIndex = prev.findIndex((item) => item.columnId === itemId);
      if (
        (itemIndex - 1 < 0 && vote === "UP") ||
        (itemIndex + 1 > allItemsList.length - 1 && vote === "DOWN")
      )
        return prev;

      if (vote === "DOWN") {
        const newArray = swap(prev, itemIndex, itemIndex + 1).map(
          (item, idx) => ({ ...item, isSelected: idx < 3 })
        );
        if (itemIndex + 1 < 3 || itemIndex < 3) {
          onClick(
            newArray.filter((item, idx) => idx < 3).map((item) => item.columnId)
          );
        }
        return newArray;
      } else {
        const newArray = swap(prev, itemIndex, itemIndex - 1).map(
          (item, idx) => ({ ...item, isSelected: idx < 3 })
        );
        if (itemIndex - 1 < 3) {
          onClick(
            newArray.filter((item, idx) => idx < 3).map((item) => item.columnId)
          );
        }
        return newArray;
      }
    });
  };

  function onDragEnd(result: DropResult) {
    if (!result.destination) {
      return;
    }
    if (result.source.index === result.destination.index) {
      return;
    }

    const newItems = reorder(
      allItemsList,
      result.source.index,
      result.destination.index
    );
    setAllItemsList(
      newItems.map((item, idx) => ({ ...item, isSelected: idx < 3 }))
    );
    onClick(
      newItems.filter((item, idx) => idx < 3).map((item) => item.columnId)
    );
  }

  return (
    <div className={`dr-flex dr-flex-col dr-rounded-8 ${className}`}>
      <div className="dr-px-16 dr-pt-14 dr-pb-8 dr-flex dr-items-center dr-justify-between">
        <div className="TouchSubheadlineRegular dr-text-darwin-touch-neutral-1000">
          {title}
        </div>
        <div className="dr-flex dr-items-center dr-text-darwin-touch-neutral-600 dr-space-x-4">
          <div className="TouchFootnoteRegular">{subtitle}</div>
          <TooltipComponent
            textTransform="none"
            placement="top"
            arrowPointAtCenter
            title={tooltipText}
          >
            <CircleInfoRegularIcon className="dr-cursor-pointer" />
          </TooltipComponent>
        </div>
      </div>
      <DragDropContext key={tab} onDragEnd={onDragEnd}>
        <div
          className={`dr-space-y-10 dr-overflow-y-scroll ${tab}-list`}
          style={{ height: `calc(80vh - 12.5rem)` }}
        >
          <Droppable droppableId="droppable">
            {(provided, snapshot) => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}
                style={getListStyle(snapshot.isDraggingOver)}
              >
                {allItemsList.map((item, index) => (
                  <Draggable
                    key={item.columnId}
                    draggableId={item.columnId}
                    index={index}
                  >
                    {(provided, snapshot, rubric) => (
                      <Item
                        onClick={handleVote}
                        tab={tab}
                        provided={provided}
                        isDragging={snapshot.isDragging}
                        item={allItemsList[rubric.source.index]}
                      />
                    )}
                  </Draggable>
                ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </div>
      </DragDropContext>
    </div>
  );
};
